import type { ItemRef, TokenSpec } from '../../shared/contract.js';
import { DT } from '../const.js';
import type { Modifier, Pipeline, StageId } from '../modifier.js';
import { hasTag } from '../token.js';
import { ZINS_CEILING } from '../tags.js';
import type { ItemDef, ItemKind } from './defs.js';
import { MANIPULATIONEN, ZINKEN } from './house.js';

/**
 * THE ITEM CATALOGUE.
 *
 * Note what is NOT here: any per-machine scripting. An enchantment item is a thing that
 * puts a *tag* on a token; the tag's behaviour lives in core/tags.ts and applies in every
 * cabinet at once. A charm is a `Modifier` — the same type the House's ZINKEN use.
 *
 * core/machines/** must never import this file. That is the covenant, and
 * test/covenant-lint.ts fails the build if it happens.
 */

export type { ItemDef, ItemKind, Rarity } from './defs.js';

const ZINS_PERIOD = 10;

const defs: ItemDef[] = [
  // ---------------------------------------------------- Der Beutel layer (enchants)
  {
    id: 'magnetschuh',
    name: 'Magnetschuh',
    kind: 'enchant',
    rarity: 'gemein',
    grantsTag: 'Magnet',
    text: 'Zieht andere Marken an.',
    combos: ['geistermuenze', 'spaltmuenze'],
    nombos: ['zinken:magnetfalle'],
  },
  {
    id: 'geistermuenze',
    name: 'Geistermünze',
    kind: 'enchant',
    rarity: 'gemein',
    grantsTag: 'Geist',
    text: 'Fällt durch den ersten Körper, den sie trifft.',
    combos: ['magnetschuh'],
    nombos: ['klebriges-pech'],
  },
  {
    id: 'gummigeld',
    name: 'Gummigeld',
    kind: 'enchant',
    rarity: 'gemein',
    grantsTag: 'Gummi',
    text: 'Superball: die ersten drei Stöße ohne Energieverlust.',
    combos: ['klebriges-pech', 'der-deckel'],
    nombos: ['schwermuenze'],
  },
  {
    id: 'klebriges-pech',
    name: 'Klebriges Pech',
    kind: 'enchant',
    rarity: 'gemein',
    grantsTag: 'Kleber',
    text: 'Bleibt kleben, wo sie landet, und wird zum Gelände.',
    combos: ['gummigeld', 'spaltmuenze'],
    nombos: ['geistermuenze'],
  },
  {
    id: 'spaltmuenze',
    name: 'Spaltmünze',
    kind: 'enchant',
    rarity: 'gemein',
    grantsTag: 'Spalt',
    text: 'Zerspringt bei Jackpot-Treffern in zwei halbe Marken.',
    combos: ['zinseszins', 'klebriges-pech'],
    nombos: ['strukturgrenze'],
  },
  {
    id: 'schwermuenze',
    name: 'Schwermünze',
    kind: 'enchant',
    rarity: 'gemein',
    grantsTag: 'Schwer',
    text: 'Dreifache Masse: pflügt durch Stifte, sinkt in Haufen.',
    combos: ['muenzschieber-lawine'],
    nombos: ['gummigeld'],
  },
  {
    id: 'zinsmuenze',
    name: 'Zinsmünze',
    kind: 'enchant',
    rarity: 'rar',
    grantsTag: 'Zins',
    text: 'Verdoppelt ihren Wert je 10 Sekunden im Automaten.',
    combos: ['spaltmuenze', 'muenzschieber-bank'],
    nombos: ['schnelles-kassieren'],
  },
  {
    id: 'echomuenze',
    name: 'Echomünze',
    kind: 'enchant',
    rarity: 'rar',
    grantsTag: 'Echo',
    text: 'Wiederholt ihren letzten Treffer 3 s später zum halben Wert.',
    combos: ['spaltmuenze', 'straehne-builds'],
    nombos: ['topf-abzug'],
  },
  {
    id: 'kassierter-fluch',
    name: 'Kassierter Fluch',
    kind: 'enchant',
    rarity: 'rar',
    grantsTag: 'Fluch',
    text: 'Alle Würfe dieser Marke laufen invertiert — sie selbst zahlt nichts.',
    combos: ['glueck-pity'],
    nombos: ['ehrliche-jackpot-builds'],
  },

  // ---------------------------------------------------- Charms (run-global)
  {
    id: 'zinseszins',
    name: 'Zinseszins',
    kind: 'charm',
    rarity: 'gemein',
    text: 'ALLE Marken reifen: ×1,5 je 10 Sekunden im Automaten.',
    combos: ['spaltmuenze', 'zinsmuenze'],
    nombos: ['schnelles-kassieren'],
    modifier: {
      id: 'zinseszins',
      source: 'charm',
      tags: ['Zins'],
      priority: 111,
      everyToken: true,
      ruleText: 'Zinseszins: jede Marke gewinnt je 10 s im Automaten die Hälfte dazu.',
      events: {
        onTokenTick: (ctx, { dt }) => {
          const t = ctx.token;
          if (!t) return;
          // tokens that already carry [Zins] use the stronger tag rule; no double-dip
          if (hasTag(t, 'Zins')) return;
          t.zinsClock += dt;
          while (t.zinsClock >= ZINS_PERIOD) {
            t.zinsClock -= ZINS_PERIOD;
            // same ceiling as the [Zins] tag default — see core/tags.ts
            if (t.faceValue >= t.baseValue * ZINS_CEILING) break;
            t.faceValue = t.faceValue * 1.5;
            ctx.api.emit({ t: 'zinsTick', token: t.id, newValue: t.faceValue });
          }
        },
      },
    },
  },
  {
    id: 'das-pendel',
    name: 'Das Pendel',
    kind: 'charm',
    rarity: 'gemein',
    text: 'Die Schwerkraft schwingt sichtbar nach links und rechts — in allen Automaten.',
    combos: ['zielskill', 'jukebox'],
    nombos: ['praezisions-setups'],
    modifier: {
      id: 'das-pendel',
      source: 'charm',
      tags: [],
      priority: 40,
      ruleText: 'Das Pendel: die Schwerkraft schwingt im Takt nach links und rechts.',
      queries: {
        gravity: (ctx, g) => {
          const seconds = ctx.tick * DT;
          const swing = Math.sin((seconds * Math.PI * 2) / 6) * 3.2;
          return { x: g.x + swing, y: g.y, z: g.z };
        },
      },
    },
  },
  {
    id: 'umgekehrte-buchfuehrung',
    name: 'Umgekehrte Buchführung',
    kind: 'charm',
    rarity: 'rar',
    text: 'Bucht die STRÄHNE zuletzt — sie vervielfacht damit auch den Topf.',
    combos: ['straehne-builds', 'topf-builds'],
    nombos: ['einzeltreffer-builds'],
    /**
     * DECISION (deviation from content/catalogue.md, recorded not taken silently):
     * the catalogue specifies "swaps the STRÄHNE and material stages". Both of those
     * stages are pure multiplications, and multiplication commutes — as written the
     * item is a guaranteed no-op, which fails item law 1 twice over (it is invisible
     * AND it is a number tweak). DER TOPF is the one ADDITIVE stage in the pipeline,
     * so the only reordering that can matter is one that crosses it. This charm
     * therefore moves STRÄHNE to after TOPF: your streak now multiplies the pot too.
     * Same fantasy, same name, same archetype — but it is actually an engine.
     */
    modifier: {
      id: 'umgekehrte-buchfuehrung',
      source: 'charm',
      tags: [],
      priority: 20,
      ruleText: 'Umgekehrte Buchführung: die STRÄHNE wird zuletzt gebucht — und vervielfacht den Topf mit.',
      queries: {
        stageOrder: (_ctx, order: StageId[]) => {
          const out: StageId[] = order.filter((s) => s !== 'straehne');
          const at = out.indexOf('topf');
          if (at < 0) return [...order];
          out.splice(at + 1, 0, 'straehne');
          return out;
        },
      },
    },
  },
  {
    id: 'der-deckel',
    name: 'Der Deckel',
    kind: 'charm',
    rarity: 'rar',
    text: 'Nimmt allen Automaten das Deckglas: Marken verlassen den Automaten in den Raum, nicht ins Nichts.',
    combos: ['gummigeld', 'alles-kinetische'],
    nombos: ['bildrate'],
    modifier: {
      id: 'der-deckel',
      source: 'charm',
      tags: [],
      priority: 30,
      ruleText: 'Der Deckel: der Saal ist ein Spiel — herausfallende Marken rollen zum Nachbarautomaten.',
      queries: {
        escape: () => 'room',
      },
    },
  },
  {
    id: 'die-quersubvention',
    name: 'Die Quersubvention',
    kind: 'charm',
    rarity: 'rar',
    text: 'Jede Niete zahlt 5 Münzen in den Topf des Automaten, an dem Sie stehen. Verlieren füllt den Topf.',
    combos: ['kassierter-fluch', 'umgekehrte-buchfuehrung'],
    nombos: ['schnelles-kassieren'],
    modifier: {
      id: 'die-quersubvention',
      source: 'charm',
      tags: [],
      priority: 118,
      ruleText: 'Die Quersubvention: jede Niete legt 5 Münzen in den Topf.',
      events: {
        // losing builds the pot. With a [Fluch] tank build this is an engine, not a
        // consolation prize — and it is the reason a curse deck has a win condition.
        onDud: (ctx) => ctx.api.addTopf(5),
      },
    },
  },
  {
    id: 'der-riesenplueschbaer',
    name: 'Der Riesenplüschbär',
    kind: 'charm',
    rarity: 'preis',
    text: 'Ein riesiger Plüschbär, im Automaten aufgestellt: eine weiche Wand, die Ihnen gehört. Was hinausfallen würde, fällt an ihm zurück.',
    combos: ['gummigeld', 'manip:bohrloch'],
    nombos: ['der-deckel'],
    modifier: {
      id: 'der-riesenplueschbaer',
      source: 'charm',
      tags: [],
      priority: 28,
      ruleText: 'Der Riesenplüschbär steht im Feld: herausfallende Marken prallen an ihm zurück.',
      queries: {
        // He resolves BEFORE Der Deckel (priority 28 vs 30), so owning both means the lid
        // is off and the bear still catches what he can reach. Two prizes, one felt rule,
        // no special case: that is the grammar doing the work.
        escape: (_ctx, v) => (v === 'room' ? v : 'block'),
      },
    },
  },
  {
    id: 'item:brecheisen',
    name: 'Brecheisen',
    kind: 'charm',
    rarity: 'preis',
    consumable: true,
    text: 'Hebelt einen entdeckten ZINKEN vom Automaten — und legt ihn in Ihren Beutel. Die Grammatik des Hauses wird Ihre.',
    combos: ['zinken:der-abschoepfer', 'zinken:gewichtete-kugel'],
    nombos: ['unentdeckte-zinken'],
    // No modifier of its own: a crowbar is a verb, not a passive. The run director
    // applies it (core/run.ts) when you use it on a cabinet whose rigging you have found.
  },
];

/**
 * ONE registry. Enchantments, charms, Manipulationen and the House's ZINKEN all live in
 * it, because to the pipeline they are one type by design. `defs` is the player's half;
 * items/house.ts is the House's. Nothing downstream needs to know which half a modifier
 * came from — which is precisely why a Brecheisen can move one across.
 */
const all: ItemDef[] = [...defs, ...MANIPULATIONEN, ...ZINKEN];

export const ITEMS: Readonly<Record<string, ItemDef>> = Object.freeze(
  Object.fromEntries(all.map((d) => [d.id, d])),
);

/** Stable, sorted list — the covenant lint greps core/machines for exactly these. */
export const ITEM_IDS: readonly string[] = Object.freeze(all.map((d) => d.id).sort());

export const ITEM_NAMES: readonly string[] = Object.freeze(all.map((d) => d.name).sort());

/** Apply an enchantment to a token spec. Max 3 slots (modifier-grammar.md). */
export function enchant(spec: TokenSpec, itemId: string): TokenSpec {
  const item = ITEMS[itemId];
  if (!item || !item.grantsTag) throw new Error(`not an enchantment: ${itemId}`);
  if (spec.tags.includes(item.grantsTag)) return spec;
  if (spec.tags.length >= 3) throw new Error(`token ${spec.id} has no free enchantment slot`);
  return { ...spec, tags: [...spec.tags, item.grantsTag] };
}

/** Resolve charm/manipulation/zinken ids into modifiers for the pipeline. */
export function modifiersFor(ids: readonly string[]): Modifier[] {
  const out: Modifier[] = [];
  for (const id of ids) {
    const item = ITEMS[id];
    if (!item) throw new Error(`unknown item: ${id}`);
    if (item.modifier) out.push(item.modifier);
  }
  return out;
}

export function itemsOfKind(kind: ItemKind): ItemDef[] {
  return all.filter((d) => d.kind === kind);
}

/**
 * contract v1: `RunState.items` replaces v0's mis-named `charms`, which actually held
 * charms, Manipulationen and ZINKEN together (they are one `Modifier` type by design).
 * The UI needs to tell them apart, so the kind and the German name travel with the id.
 */
export function itemRefs(ids: readonly string[], pipe?: Pipeline): ItemRef[] {
  const out: ItemRef[] = [];
  for (const id of ids) {
    const item = ITEMS[id];
    if (!item) continue;
    const mod = item.modifier;
    const live = pipe && mod ? pipe.find(mod.id) : null;
    out.push({
      id,
      name: item.name,
      kind: item.kind,
      ...(mod?.machines?.[0] ? { machine: mod.machines[0] } : {}),
      ...(item.kind === 'zinken' ? { discovered: live ? live.discovered === true : false } : {}),
    });
  }
  return out;
}
